import { BreakpointState, MediaMatcher } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    Injector,
    PLATFORM_ID,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FuseConfigService } from '@fuse/services/config';
import { Observable } from 'rxjs';

type MediaQueryObserver = {
    matches: WritableSignal<boolean>;
    handler: (event: MediaQueryListEvent) => void;
};

@Injectable({ providedIn: 'root' })
export class FuseMediaWatcherService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly mediaMatcher = inject(MediaMatcher);
    private readonly injector = inject(Injector);
    private readonly fuseConfigService = inject(FuseConfigService);

    private readonly matchers = new Map<string, MediaQueryObserver>();

    // Convert BehaviorSubject to signal; requireSync is safe since BehaviorSubject emits immediately
    private readonly config = toSignal(this.fuseConfigService.config$, { requireSync: true });

    // Core computed: derives matchingAliases/matchingQueries reactively from match() signals
    private readonly mediaChange = computed(() => {
        const screens = this.config().screens as Record<string, string>;
        const matchingAliases: string[] = [];
        const matchingQueries: Record<string, string> = {};

        for (const [alias, width] of Object.entries(screens)) {
            const query = `(min-width: ${width})`;
            if (this.match(query)()) {
                matchingAliases.push(alias);
                matchingQueries[alias] = query;
            }
        }

        return { matchingAliases, matchingQueries };
    });

    // Observable adapter — uses ReplaySubject(1) internally via toObservable
    public readonly onMediaChange$: Observable<{ matchingAliases: string[]; matchingQueries: any }> = toObservable(
        this.mediaChange,
    );

    public onMediaQueryChange$(query: string | string[]): Observable<BreakpointState> {
        const queries = Array.isArray(query) ? query : [query];
        const state = computed(() => {
            const breakpoints: Record<string, boolean> = {};
            let anyMatch = false;
            for (const q of queries) {
                const isMatch = this.match(q)();
                breakpoints[q] = isMatch;
                if (isMatch) anyMatch = true;
            }
            return { matches: anyMatch, breakpoints };
        });
        return toObservable(state, { injector: this.injector });
    }

    public match(query: string): Signal<boolean> {
        const existingMatcher = this.matchers.get(query);
        if (existingMatcher) {
            return existingMatcher.matches.asReadonly();
        }

        const mql = this.mediaMatcher.matchMedia(query);
        const matches = signal(mql.matches);
        const handler = (event: MediaQueryListEvent) => {
            matches.set(event.matches);
        };

        if (this.isBrowser) {
            mql.addEventListener('change', handler);
            this.destroyRef.onDestroy(() => {
                mql.removeEventListener('change', handler);
            });
        }

        this.matchers.set(query, { matches, handler });

        return matches.asReadonly();
    }
}
